import { useState, useEffect } from "react";
import { Globe, Battery, FileCheck, ChevronDown, ExternalLink, Download, Cpu } from "lucide-react";
import axios from "axios";
import { API } from "../config";

const LOJACK_LOGO = "/lojack_logo_white.png";

const COUNTRIES = [
  { code: 'IT', name: 'Italia', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'FR', name: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ES', name: 'Espana', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'DE', name: 'Deutschland', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'UK', name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'PT', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'BE', name: 'Belgique', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'NL', name: 'Nederland', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'PL', name: 'Polska', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'SE', name: 'Sverige', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'NO', name: 'Norge', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'FI', name: 'Suomi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'DK', name: 'Danmark', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: 'CZ', name: 'Ceska republika', flag: '\u{1F1E8}\u{1F1FF}' },
  { code: 'GR', name: 'Ellada', flag: '\u{1F1EC}\u{1F1F7}' },
  { code: 'CH', name: 'Schweiz', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'GBZ', name: 'Gibraltar', flag: '\u{1F1EC}\u{1F1EE}' },
  { code: 'IRL', name: 'Ireland', flag: '\u{1F1EE}\u{1F1EA}' },
];

const TRANSLATIONS = {
  IT: {
    title: "Informazioni Tecniche",
    subtitle: "Prodotto LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Dichiarazione di Conformità",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Batteria Device",
    battery_desc: "Le batterie sono provviste di un'etichetta contenente le informazioni generali conformi al Regolamento UE 2023/1542 relativo alle batterie e ai rifiuti di batterie.",
    conformity_section: "Dichiarazione di Conformità",
    conformity_placeholder: "Contenuto in fase di definizione. La dichiarazione di conformità sarà disponibile a breve.",
    manufacturer: "Nome produttore",
    trademark: "Marchio registrato",
    address: "Indirizzo postale",
    website: "Indirizzo web",
    email: "Indirizzo email",
    model: "Identificazione del modello",
    place_manufacture: "Luogo di fabbricazione",
    weight: "Peso",
    capacity: "Capacita",
    chemistry: "Composizione chimica",
    hazardous: "Sostanze pericolose",
    extinguishing: "Agente estinguente",
    critical_materials: "Materie prime critiche",
    labeling_title: "Requisiti di etichettatura",
    labeling_items: [
      "Le batterie portatili non ricaricabili sono provviste di un'etichetta contenente le informazioni sulla durata media minima quando usate in applicazioni specifiche e di un'etichetta con l'indicazione \"non ricaricabile\"",
      "Tutte le batterie sono contrassegnate con il simbolo per la raccolta differenziata delle batterie",
      "Tutte le batterie sono contrassegnate con un codice QR che permette di accedere ad una pagina web tradotta nella lingua locale dello Stato Membro"
    ],
    qr_info: "Il codice QR permette di accedere a: informazioni presenti sull'etichetta o sul packaging, dichiarazione di conformita, relazione sulla strategia relativa alla due diligence, informazioni relative alla prevenzione e alla gestione dei rifiuti",
    footer: "Tutti i diritti riservati",
    reg_reference: "Reg. UE 2023/1542",
    data_section: "Dati da comunicare",
    not_available: "Da definire",
  },
  UK: {
    title: "Technical Information",
    subtitle: "LoJack Tracker Product - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaration of Conformity",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Device Battery",
    battery_desc: "Batteries are provided with a label containing general information in compliance with EU Regulation 2023/1542 concerning batteries and waste batteries.",
    conformity_section: "Declaration of Conformity",
    conformity_placeholder: "Content under preparation. The declaration of conformity will be available shortly.",
    manufacturer: "Manufacturer name",
    trademark: "Registered trademark",
    address: "Postal address",
    website: "Web address",
    email: "Email address",
    model: "Model identification",
    place_manufacture: "Place of manufacture",
    weight: "Weight",
    capacity: "Capacity",
    chemistry: "Chemical composition",
    hazardous: "Hazardous substances",
    extinguishing: "Extinguishing agent",
    critical_materials: "Critical raw materials",
    labeling_title: "Labeling requirements",
    labeling_items: [
      "Non-rechargeable portable batteries are provided with a label containing information on the minimum average duration when used in specific applications and a label indicating \"non-rechargeable\"",
      "All batteries are marked with the symbol for separate collection of batteries",
      "All batteries are marked with a QR code providing access to a web page translated into the local language of the Member State"
    ],
    qr_info: "The QR code provides access to: information present on the label or packaging, declaration of conformity, report on the due diligence strategy, information on waste prevention and management",
    footer: "All rights reserved",
    reg_reference: "EU Reg. 2023/1542",
    data_section: "Data to communicate",
    not_available: "To be defined",
  },
  FR: {
    title: "Informations Techniques",
    subtitle: "Produit LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaration de Conformite",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Batterie de l'appareil",
    battery_desc: "Les batteries sont munies d'une etiquette contenant les informations generales conformement au Reglement UE 2023/1542 relatif aux batteries et aux dechets de batteries.",
    conformity_section: "Declaration de Conformite",
    conformity_placeholder: "Contenu en cours de preparation. La declaration de conformite sera disponible prochainement.",
    manufacturer: "Nom du fabricant",
    trademark: "Marque deposee",
    address: "Adresse postale",
    website: "Adresse web",
    email: "Adresse email",
    model: "Identification du modele",
    place_manufacture: "Lieu de fabrication",
    weight: "Poids",
    capacity: "Capacite",
    chemistry: "Composition chimique",
    hazardous: "Substances dangereuses",
    extinguishing: "Agent extincteur",
    critical_materials: "Matieres premieres critiques",
    labeling_title: "Exigences d'etiquetage",
    labeling_items: [
      "Les batteries portables non rechargeables sont munies d'une etiquette contenant des informations sur la duree moyenne minimale en usage specifique et d'une etiquette indiquant \"non rechargeable\"",
      "Toutes les batteries portent le symbole de collecte separee des batteries",
      "Toutes les batteries portent un code QR permettant d'acceder a une page web traduite dans la langue locale de l'Etat Membre"
    ],
    qr_info: "Le code QR permet d'acceder a: informations presentes sur l'etiquette ou l'emballage, declaration de conformite, rapport sur la strategie de diligence raisonnable, informations sur la prevention et la gestion des dechets",
    footer: "Tous droits reserves",
    reg_reference: "Reg. UE 2023/1542",
    data_section: "Donnees a communiquer",
    not_available: "A definir",
  },
  ES: {
    title: "Informacion Tecnica",
    subtitle: "Producto LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaracion de Conformidad",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Bateria del dispositivo",
    battery_desc: "Las baterias estan provistas de una etiqueta con la informacion general conforme al Reglamento UE 2023/1542 relativo a las baterias y residuos de baterias.",
    conformity_section: "Declaracion de Conformidad",
    conformity_placeholder: "Contenido en preparacion. La declaracion de conformidad estara disponible en breve.",
    manufacturer: "Nombre del fabricante",
    trademark: "Marca registrada",
    address: "Direccion postal",
    website: "Direccion web",
    email: "Direccion email",
    model: "Identificacion del modelo",
    place_manufacture: "Lugar de fabricacion",
    weight: "Peso",
    capacity: "Capacidad",
    chemistry: "Composicion quimica",
    hazardous: "Sustancias peligrosas",
    extinguishing: "Agente extintor",
    critical_materials: "Materias primas criticas",
    labeling_title: "Requisitos de etiquetado",
    labeling_items: [
      "Las baterias portatiles no recargables estan provistas de una etiqueta con informacion sobre la duracion media minima y una etiqueta que indica \"no recargable\"",
      "Todas las baterias estan marcadas con el simbolo de recogida selectiva de baterias",
      "Todas las baterias estan marcadas con un codigo QR que permite acceder a una pagina web traducida al idioma local del Estado Miembro"
    ],
    qr_info: "El codigo QR permite acceder a: informacion presente en la etiqueta o embalaje, declaracion de conformidad, informe sobre la estrategia de diligencia debida, informacion sobre prevencion y gestion de residuos",
    footer: "Todos los derechos reservados",
    reg_reference: "Reg. UE 2023/1542",
    data_section: "Datos a comunicar",
    not_available: "Por definir",
  },
  DE: {
    title: "Technische Informationen",
    subtitle: "LoJack Tracker Produkt - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Konformitatserklarung",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Geratebatterie",
    battery_desc: "Die Batterien sind mit einem Etikett versehen, das allgemeine Informationen gemaess der EU-Verordnung 2023/1542 uber Batterien und Altbatterien enthalt.",
    conformity_section: "Konformitatserklarung",
    conformity_placeholder: "Inhalt in Vorbereitung. Die Konformitatserklarung wird in Kurze verfugbar sein.",
    manufacturer: "Herstellername",
    trademark: "Eingetragene Marke",
    address: "Postanschrift",
    website: "Webadresse",
    email: "E-Mail-Adresse",
    model: "Modellkennung",
    place_manufacture: "Herstellungsort",
    weight: "Gewicht",
    capacity: "Kapazitat",
    chemistry: "Chemische Zusammensetzung",
    hazardous: "Gefahrliche Stoffe",
    extinguishing: "Loeschmittel",
    critical_materials: "Kritische Rohstoffe",
    labeling_title: "Kennzeichnungsanforderungen",
    labeling_items: [
      "Nicht wiederaufladbare tragbare Batterien sind mit einem Etikett versehen, das Informationen uber die durchschnittliche Mindestlebensdauer enthalt, sowie mit dem Hinweis \"nicht wiederaufladbar\"",
      "Alle Batterien tragen das Symbol fur die getrennte Sammlung von Batterien",
      "Alle Batterien tragen einen QR-Code, der Zugang zu einer in die Landessprache des Mitgliedstaats ubersetzten Webseite bietet"
    ],
    qr_info: "Der QR-Code bietet Zugang zu: Informationen auf dem Etikett oder der Verpackung, Konformitatserklarung, Bericht uber die Sorgfaltspflichtstrategie, Informationen zur Abfallvermeidung und -bewirtschaftung",
    footer: "Alle Rechte vorbehalten",
    reg_reference: "EU-Verordnung 2023/1542",
    data_section: "Mitzuteilende Daten",
    not_available: "Noch festzulegen",
  },
  PT: {
    title: "Informacoes Tecnicas",
    subtitle: "Produto LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaracao de Conformidade",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Bateria do dispositivo",
    battery_desc: "As baterias sao fornecidas com uma etiqueta contendo informacoes gerais em conformidade com o Regulamento UE 2023/1542 relativo a baterias e residuos de baterias.",
    conformity_section: "Declaracao de Conformidade",
    conformity_placeholder: "Conteudo em preparacao. A declaracao de conformidade estara disponivel em breve.",
    manufacturer: "Nome do fabricante", trademark: "Marca registada", address: "Endereco postal",
    website: "Endereco web", email: "Endereco email", model: "Identificacao do modelo",
    place_manufacture: "Local de fabrico",
    weight: "Peso", capacity: "Capacidade", chemistry: "Composicao quimica",
    hazardous: "Substancias perigosas", extinguishing: "Agente extintor",
    critical_materials: "Materias-primas criticas",
    labeling_title: "Requisitos de rotulagem",
    labeling_items: ["As baterias portateis nao recarregaveis possuem rotulagem com duracao media minima e indicacao \"nao recarregavel\"", "Todas as baterias possuem o simbolo de recolha seletiva", "Todas as baterias possuem codigo QR com acesso a pagina web traduzida"],
    qr_info: "O codigo QR permite aceder a informacoes da etiqueta, declaracao de conformidade, relatorio de diligencia devida e informacoes sobre gestao de residuos",
    footer: "Todos os direitos reservados", reg_reference: "Reg. UE 2023/1542",
    data_section: "Dados a comunicar", not_available: "A definir",
  },
  NL: {
    title: "Technische Informatie",
    subtitle: "LoJack Tracker Product - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Conformiteitsverklaring",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Apparaatbatterij",
    battery_desc: "De batterijen zijn voorzien van een etiket met algemene informatie in overeenstemming met EU-Verordening 2023/1542 betreffende batterijen en afgedankte batterijen.",
    conformity_section: "Conformiteitsverklaring",
    conformity_placeholder: "Inhoud in voorbereiding. De conformiteitsverklaring zal binnenkort beschikbaar zijn.",
    manufacturer: "Naam fabrikant", trademark: "Geregistreerd handelsmerk", address: "Postadres",
    website: "Webadres", email: "E-mailadres", model: "Modelidentificatie",
    place_manufacture: "Plaats van fabricage",
    weight: "Gewicht", capacity: "Capaciteit", chemistry: "Chemische samenstelling",
    hazardous: "Gevaarlijke stoffen", extinguishing: "Blusmiddel",
    critical_materials: "Kritieke grondstoffen",
    labeling_title: "Etiketteringsvereisten",
    labeling_items: ["Niet-oplaadbare draagbare batterijen zijn voorzien van een etiket met informatie over de gemiddelde minimale levensduur en de aanduiding \"niet-oplaadbaar\"", "Alle batterijen zijn gemarkeerd met het symbool voor gescheiden inzameling van batterijen", "Alle batterijen zijn gemarkeerd met een QR-code die toegang biedt tot een webpagina vertaald in de lokale taal van de lidstaat"],
    qr_info: "De QR-code biedt toegang tot: informatie op het etiket of de verpakking, conformiteitsverklaring, verslag over de due diligence-strategie, informatie over afvalpreventie en -beheer",
    footer: "Alle rechten voorbehouden", reg_reference: "EU-Verordening 2023/1542",
    data_section: "Te communiceren gegevens", not_available: "Nog te bepalen",
  },
  PL: {
    title: "Informacje Techniczne",
    subtitle: "Produkt LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Deklaracja Zgodnosci",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Bateria urzadzenia",
    battery_desc: "Baterie sa opatrzone etykieta zawierajaca informacje ogolne zgodnie z Rozporzadzeniem UE 2023/1542 dotyczacym baterii i zuzytych baterii.",
    conformity_section: "Deklaracja Zgodnosci",
    conformity_placeholder: "Tresc w przygotowaniu. Deklaracja zgodnosci bedzie dostepna wkrotce.",
    manufacturer: "Nazwa producenta", trademark: "Zarejestrowany znak towarowy", address: "Adres pocztowy",
    website: "Adres strony internetowej", email: "Adres e-mail", model: "Identyfikacja modelu",
    place_manufacture: "Miejsce produkcji",
    weight: "Waga", capacity: "Pojemnosc", chemistry: "Sklad chemiczny",
    hazardous: "Substancje niebezpieczne", extinguishing: "Srodek gasniczy",
    critical_materials: "Krytyczne surowce",
    labeling_title: "Wymagania dotyczace etykietowania",
    labeling_items: ["Nieladowalne baterie przenosne sa opatrzone etykieta z informacja o minimalnej sredniej trwalosci oraz oznaczeniem \"nieladowalna\"", "Wszystkie baterie sa oznaczone symbolem selektywnej zbiorki baterii", "Wszystkie baterie sa oznaczone kodem QR umozliwiajacym dostep do strony internetowej przetlumaczonej na jezyk lokalny panstwa czlonkowskiego"],
    qr_info: "Kod QR umozliwia dostep do: informacji z etykiety lub opakowania, deklaracji zgodnosci, raportu dotyczacego strategii nalezytej starannosci, informacji o zapobieganiu powstawaniu odpadow i gospodarowaniu nimi",
    footer: "Wszelkie prawa zastrzezone", reg_reference: "Rozp. UE 2023/1542",
    data_section: "Dane do przekazania", not_available: "Do ustalenia",
  },
  SE: {
    title: "Teknisk Information",
    subtitle: "LoJack Tracker Produkt - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Forsakran om Overensstammelse",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Enhetsbatteri",
    battery_desc: "Batterierna ar forsedda med en etikett som innehaller allman information i enlighet med EU-forordning 2023/1542 om batterier och batteriavfall.",
    conformity_section: "Forsakran om Overensstammelse",
    conformity_placeholder: "Innehall under forberedelse. Forsakran om overensstammelse kommer att finnas tillganglig inom kort.",
    manufacturer: "Tillverkarens namn", trademark: "Registrerat varumarke", address: "Postadress",
    website: "Webbadress", email: "E-postadress", model: "Modellidentifiering",
    place_manufacture: "Tillverkningsplats",
    weight: "Vikt", capacity: "Kapacitet", chemistry: "Kemisk sammansattning",
    hazardous: "Farliga amnen", extinguishing: "Slackmedel",
    critical_materials: "Kritiska ravaror",
    labeling_title: "Markningskrav",
    labeling_items: ["Icke-laddningsbara barbare batterier ar forsedda med en etikett med information om minsta genomsnittliga livslangd och markningen \"icke-laddningsbart\"", "Alla batterier ar markta med symbolen for separat insamling av batterier", "Alla batterier ar markta med en QR-kod som ger tillgang till en webbsida oversatt till det lokala spraket i medlemsstaten"],
    qr_info: "QR-koden ger tillgang till: information pa etiketten eller forpackningen, forsakran om overensstammelse, rapport om strategin for tillborlig aktsamhet, information om forebyggande och hantering av avfall",
    footer: "Alla rattigheter forbehallna", reg_reference: "EU-forordning 2023/1542",
    data_section: "Uppgifter att kommunicera", not_available: "Att faststalla",
  },
  NO: {
    title: "Teknisk Informasjon",
    subtitle: "LoJack Tracker Produkt - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Samsvarserklaering",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Enhetsbatteri",
    battery_desc: "Batteriene er utstyrt med en etikett som inneholder generell informasjon i samsvar med EU-forordning 2023/1542 om batterier og batteriavfall.",
    conformity_section: "Samsvarserklaering",
    conformity_placeholder: "Innhold under utarbeidelse. Samsvarserklaering vil vaere tilgjengelig snart.",
    manufacturer: "Produsentnavn", trademark: "Registrert varemerke", address: "Postadresse",
    website: "Nettadresse", email: "E-postadresse", model: "Modellidentifikasjon",
    place_manufacture: "Produksjonssted",
    weight: "Vekt", capacity: "Kapasitet", chemistry: "Kjemisk sammensetning",
    hazardous: "Farlige stoffer", extinguishing: "Slukkemiddel",
    critical_materials: "Kritiske raastoffer",
    labeling_title: "Merkingskrav",
    labeling_items: ["Ikke-oppladbare baerbare batterier er utstyrt med en etikett med informasjon om minste gjennomsnittlige levetid og merket \"ikke-oppladbart\"", "Alle batterier er merket med symbolet for separat innsamling av batterier", "Alle batterier er merket med en QR-kode som gir tilgang til en nettside oversatt til det lokale spraket i medlemsstaten"],
    qr_info: "QR-koden gir tilgang til: informasjon pa etiketten eller emballasjen, samsvarserklaering, rapport om tilborlig aktsomhetsstrategi, informasjon om avfallsforebygging og -handtering",
    footer: "Alle rettigheter forbeholdt", reg_reference: "EU-forordning 2023/1542",
    data_section: "Data som skal kommuniseres", not_available: "Fastsettes",
  },
  FI: {
    title: "Tekniset Tiedot",
    subtitle: "LoJack Tracker -tuote - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Vaatimustenmukaisuusvakuutus",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Laitteen akku",
    battery_desc: "Akut on varustettu etiketilla, joka sisaltaa yleiset tiedot EU-asetuksen 2023/1542 mukaisesti, joka koskee akkuja ja akkujatteita.",
    conformity_section: "Vaatimustenmukaisuusvakuutus",
    conformity_placeholder: "Sisaltoa valmistellaan. Vaatimustenmukaisuusvakuutus on saatavilla pian.",
    manufacturer: "Valmistajan nimi", trademark: "Rekisteroity tavaramerkki", address: "Postiosoite",
    website: "Verkko-osoite", email: "Sahkopostiosoite", model: "Mallin tunniste",
    place_manufacture: "Valmistuspaikka",
    weight: "Paino", capacity: "Kapasiteetti", chemistry: "Kemiallinen koostumus",
    hazardous: "Vaaralliset aineet", extinguishing: "Sammutusaine",
    critical_materials: "Kriittiset raaka-aineet",
    labeling_title: "Merkintavaatimukset",
    labeling_items: ["Ei-ladattavat kannettavat akut on varustettu etiketilla, jossa on tiedot vahimmaiskestavyydesta ja merkinta \"ei-ladattava\"", "Kaikissa akuissa on erilliskerayssymboli", "Kaikissa akuissa on QR-koodi, joka antaa paasyn jasenvaltion paikalliselle kielelle kaannetylle verkkosivulle"],
    qr_info: "QR-koodi antaa paasyn: etiketin tai pakkauksen tietoihin, vaatimustenmukaisuusvakuutukseen, huolellisuusstrategiaraporttiin, jatteen ehkaisya ja hallintaa koskeviin tietoihin",
    footer: "Kaikki oikeudet pidatetaan", reg_reference: "EU-asetus 2023/1542",
    data_section: "Ilmoitettavat tiedot", not_available: "Maariteltavana",
  },
  DK: {
    title: "Teknisk Information",
    subtitle: "LoJack Tracker Produkt - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Overensstemmelseserklaring",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Enhedsbatteri",
    battery_desc: "Batterierne er forsynet med en etiket med generelle oplysninger i overensstemmelse med EU-forordning 2023/1542 om batterier og batteriaffald.",
    conformity_section: "Overensstemmelseserklearing",
    conformity_placeholder: "Indhold under udarbejdelse. Overensstemmelseserklaeringen vil vaere tilgaengelig snart.",
    manufacturer: "Producentnavn", trademark: "Registreret varemaerke", address: "Postadresse",
    website: "Webadresse", email: "E-mailadresse", model: "Modelidentifikation",
    place_manufacture: "Fremstillingssted",
    weight: "Vaegt", capacity: "Kapacitet", chemistry: "Kemisk sammensaetning",
    hazardous: "Farlige stoffer", extinguishing: "Slukningsmiddel",
    critical_materials: "Kritiske raastoffer",
    labeling_title: "Maerkningskrav",
    labeling_items: ["Ikke-genopladelige baerbare batterier er forsynet med en etiket med oplysninger om mindste gennemsnitlige levetid og maerkningen \"ikke-genopladeligt\"", "Alle batterier er maerket med symbolet for saerskilt indsamling af batterier", "Alle batterier er maerket med en QR-kode, der giver adgang til en webside oversat til det lokale sprog i medlemsstaten"],
    qr_info: "QR-koden giver adgang til: oplysninger paa etiketten eller emballagen, overensstemmelseserklaeering, rapport om due diligence-strategi, oplysninger om affaldsforebyggelse og -haandtering",
    footer: "Alle rettigheder forbeholdes", reg_reference: "EU-forordning 2023/1542",
    data_section: "Data der skal kommunikeres", not_available: "Skal fastlaegges",
  },
  CZ: {
    title: "Technicke Informace",
    subtitle: "Produkt LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Prohlaseni o shode",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Baterie zarizeni",
    battery_desc: "Baterie jsou opatreny stitkem obsahujicim obecne informace v souladu s narizenim EU 2023/1542 o bateriich a odpadnich bateriich.",
    conformity_section: "Prohlaseni o Shode",
    conformity_placeholder: "Obsah se pripravuje. Prohlaseni o shode bude k dispozici v kratke dobe.",
    manufacturer: "Nazev vyrobce", trademark: "Registrovana ochrana znamka", address: "Postovni adresa",
    website: "Webova adresa", email: "E-mailova adresa", model: "Identifikace modelu",
    place_manufacture: "Misto vyroby",
    weight: "Hmotnost", capacity: "Kapacita", chemistry: "Chemicke slozeni",
    hazardous: "Nebezpecne latky", extinguishing: "Hasici prostredek",
    critical_materials: "Kriticke suroviny",
    labeling_title: "Pozadavky na oznacovani",
    labeling_items: ["Nedobijeci prenosne baterie jsou opatreny stitkem s informacemi o minimalni prumerne zivotnosti a oznacenim \"nedobijeci\"", "Vsechny baterie jsou oznaceny symbolem pro trideny sber baterii", "Vsechny baterie jsou oznaceny QR kodem umoznujicim pristup k webove strance prelozene do mistniho jazyka clenskeho statu"],
    qr_info: "QR kod umoznuje pristup k: informacim na stitku nebo obalu, prohlaseni o shode, zprave o strategii nalezite pece, informacim o predchazeni vzniku odpadu a nakladani s nim",
    footer: "Vsechna prava vyhrazena", reg_reference: "Narizeni EU 2023/1542",
    data_section: "Udaje ke sdeleni", not_available: "Bude urceno",
  },
  GR: {
    title: "Technikes Pliroforios",
    subtitle: "Proion LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Dilosi Symmorfosis",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Bataria Syskevis",
    battery_desc: "Oi bataries ferous etiketa pou periechei genikes pliroforios simfona me ton Kanonismo EE 2023/1542 schetika me tis bataries kai ta apovlita ton batarion.",
    conformity_section: "Dilosi Simorfosis",
    conformity_placeholder: "To periechomeno vrisketai ypo proetoimasia. I dilosi simorfosis tha einai diathesimi syntoma.",
    manufacturer: "Onoma kataskevasth", trademark: "Katatetheimeno emporiko sima", address: "Tachydromiki diefthinsi",
    website: "Diefthinsi istoselidads", email: "Diefthinsi ilektronikou tachydromiou", model: "Anagnoristiko montelo",
    place_manufacture: "Topos kataskevis",
    weight: "Varos", capacity: "Choritikotita", chemistry: "Chimiki synthesi",
    hazardous: "Epikindynes ousies", extinguishing: "Pyrosvstiko meso",
    critical_materials: "Krissimes protes yles",
    labeling_title: "Apaitiseis episimansis",
    labeling_items: ["Oi mi epanafortizomenes fortes bataries ferous etiketa me pliroforios gia tin elachisti mesi diarkeia kai tin endeixi \"mi epanafortizomeni\"", "Oles oi bataries feroun to symvolo gia tin choristiki syllogi ton batarion", "Oles oi bataries feroun enan kodiko QR pou parechei prosvasi se mia istoselida metafrasmeni stin topiki glossa tou kratous melous"],
    qr_info: "O kodikas QR parechei prosvasi se: pliroforios stin etiketa i ti syskeasia, dilosi simorfosis, ekthesi gia ti stratigiki deontos epimelias, pliroforios gia tin prolepsi kai diacheirisi apoviton",
    footer: "Ola ta dikaiomata diatiountai", reg_reference: "Kanonismos EE 2023/1542",
    data_section: "Dedomena pros koinopoiisi", not_available: "Pros kathorismo",
  },
  CH: {
    title: "Technische Informationen",
    subtitle: "LoJack Tracker Produkt - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Konformitatserklarung",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Geratebatterie",
    battery_desc: "Die Batterien sind mit einem Etikett versehen, das allgemeine Informationen gemaess der EU-Verordnung 2023/1542 uber Batterien und Altbatterien enthalt.",
    conformity_section: "Konformitatserklarung",
    conformity_placeholder: "Inhalt in Vorbereitung. Die Konformitatserklarung wird in Kurze verfugbar sein.",
    manufacturer: "Herstellername", trademark: "Eingetragene Marke", address: "Postanschrift",
    website: "Webadresse", email: "E-Mail-Adresse", model: "Modellkennung",
    place_manufacture: "Herstellungsort",
    weight: "Gewicht", capacity: "Kapazitat", chemistry: "Chemische Zusammensetzung",
    hazardous: "Gefahrliche Stoffe", extinguishing: "Loeschmittel",
    critical_materials: "Kritische Rohstoffe",
    labeling_title: "Kennzeichnungsanforderungen",
    labeling_items: ["Nicht wiederaufladbare tragbare Batterien sind mit einem Etikett versehen, das Informationen uber die durchschnittliche Mindestlebensdauer enthalt, sowie mit dem Hinweis \"nicht wiederaufladbar\"", "Alle Batterien tragen das Symbol fur die getrennte Sammlung von Batterien", "Alle Batterien tragen einen QR-Code, der Zugang zu einer in die Landessprache des Mitgliedstaats ubersetzten Webseite bietet"],
    qr_info: "Der QR-Code bietet Zugang zu: Informationen auf dem Etikett oder der Verpackung, Konformitatserklarung, Bericht uber die Sorgfaltspflichtstrategie, Informationen zur Abfallvermeidung und -bewirtschaftung",
    footer: "Alle Rechte vorbehalten", reg_reference: "EU-Verordnung 2023/1542",
    data_section: "Mitzuteilende Daten", not_available: "Noch festzulegen",
  },
  BE: {
    title: "Informations Techniques",
    subtitle: "Produit LoJack Tracker - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaration de Conformite",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Batterie de l'appareil",
    battery_desc: "Les batteries sont munies d'une etiquette contenant les informations generales conformement au Reglement UE 2023/1542 relatif aux batteries et aux dechets de batteries.",
    conformity_section: "Declaration de Conformite",
    conformity_placeholder: "Contenu en cours de preparation. La declaration de conformite sera disponible prochainement.",
    manufacturer: "Nom du fabricant", trademark: "Marque deposee", address: "Adresse postale",
    website: "Adresse web", email: "Adresse email", model: "Identification du modele",
    place_manufacture: "Lieu de fabrication",
    weight: "Poids", capacity: "Capacite", chemistry: "Composition chimique",
    hazardous: "Substances dangereuses", extinguishing: "Agent extincteur",
    critical_materials: "Matieres premieres critiques",
    labeling_title: "Exigences d'etiquetage",
    labeling_items: ["Les batteries portables non rechargeables sont munies d'une etiquette contenant des informations sur la duree moyenne minimale en usage specifique et d'une etiquette indiquant \"non rechargeable\"", "Toutes les batteries portent le symbole de collecte separee des batteries", "Toutes les batteries portent un code QR permettant d'acceder a une page web traduite dans la langue locale de l'Etat Membre"],
    qr_info: "Le code QR permet d'acceder a: informations presentes sur l'etiquette ou l'emballage, declaration de conformite, rapport sur la strategie de diligence raisonnable, informations sur la prevention et la gestion des dechets",
    footer: "Tous droits reserves", reg_reference: "Reg. UE 2023/1542",
    data_section: "Donnees a communiquer", not_available: "A definir",
  },
  GBZ: {
    title: "Technical Information",
    subtitle: "LoJack Tracker Product - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaration of Conformity",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Device Battery",
    battery_desc: "Batteries are provided with a label containing general information in compliance with EU Regulation 2023/1542 concerning batteries and waste batteries.",
    conformity_section: "Declaration of Conformity",
    conformity_placeholder: "Content under preparation. The declaration of conformity will be available shortly.",
    manufacturer: "Manufacturer name", trademark: "Registered trademark", address: "Postal address",
    website: "Web address", email: "Email address", model: "Model identification",
    place_manufacture: "Place of manufacture",
    weight: "Weight", capacity: "Capacity", chemistry: "Chemical composition",
    hazardous: "Hazardous substances", extinguishing: "Extinguishing agent",
    critical_materials: "Critical raw materials",
    labeling_title: "Labeling requirements",
    labeling_items: ["Non-rechargeable portable batteries are provided with a label containing information on the minimum average duration when used in specific applications and a label indicating \"non-rechargeable\"", "All batteries are marked with the symbol for separate collection of batteries", "All batteries are marked with a QR code providing access to a web page translated into the local language of the Member State"],
    qr_info: "The QR code provides access to: information present on the label or packaging, declaration of conformity, report on the due diligence strategy, information on waste prevention and management",
    footer: "All rights reserved", reg_reference: "EU Reg. 2023/1542",
    data_section: "Data to communicate", not_available: "To be defined",
  },
  IRL: {
    title: "Technical Information",
    subtitle: "LoJack Tracker Product - ATU1650MB",
    device_section: "ATU1650MB",
    doc_conformity: "Declaration of Conformity",
    doc_vca: "Vehicle Certification Agency",
    battery_section: "Device Battery",
    battery_desc: "Batteries are provided with a label containing general information in compliance with EU Regulation 2023/1542 concerning batteries and waste batteries.",
    conformity_section: "Declaration of Conformity",
    conformity_placeholder: "Content under preparation. The declaration of conformity will be available shortly.",
    manufacturer: "Manufacturer name", trademark: "Registered trademark", address: "Postal address",
    website: "Web address", email: "Email address", model: "Model identification",
    place_manufacture: "Place of manufacture",
    weight: "Weight", capacity: "Capacity", chemistry: "Chemical composition",
    hazardous: "Hazardous substances", extinguishing: "Extinguishing agent",
    critical_materials: "Critical raw materials",
    labeling_title: "Labeling requirements",
    labeling_items: ["Non-rechargeable portable batteries are provided with a label containing information on the minimum average duration when used in specific applications and a label indicating \"non-rechargeable\"", "All batteries are marked with the symbol for separate collection of batteries", "All batteries are marked with a QR code providing access to a web page translated into the local language of the Member State"],
    qr_info: "The QR code provides access to: information present on the label or packaging, declaration of conformity, report on the due diligence strategy, information on waste prevention and management",
    footer: "All rights reserved", reg_reference: "EU Reg. 2023/1542",
    data_section: "Data to communicate", not_available: "To be defined",
  },
};

// All 18 countries have dedicated translations
const getTranslation = (country) => {
  return TRANSLATIONS[country] || TRANSLATIONS['IT'];
};

const BATTERY_DATA_DEFAULTS = {
  manufacturer: "Great Power",
  trademark: "Great Power",
  address: "912 Xicun Section, Shiliang Road, Shawan Town, Panyu, Guangzhou, GD, PRC",
  website: "www.greatpower.net",
  email: "sales@greatpower.net",
  model: "CP6238 40-2",
  place_manufacture: "PRC",
  weight: "38 g",
  capacity: "4400 mAh",
  chemistry: "Litio-Manganese-Diossido (LiMnO2)",
  hazardous: null,
  extinguishing: null,
  critical_materials: null,
};

const TechnicalInfoPage = () => {
  const [selectedCountry, setSelectedCountry] = useState('IT');
  const [batteryData, setBatteryData] = useState(BATTERY_DATA_DEFAULTS);
  const [conformity, setConformity] = useState({ content: "" });
  const t = getTranslation(selectedCountry);
  const currentCountry = COUNTRIES.find(c => c.code === selectedCountry);

  useEffect(() => {
    axios.get(`${API}/technical-info`)
      .then(res => {
        if (res.data.battery_data) setBatteryData(res.data.battery_data);
        if (res.data.conformity) setConformity(res.data.conformity);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="technical-info-page">
      {/* Header */}
      <header className="bg-[#111] border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-10 sm:h-12" />
          </div>
          
          {/* Country Selector */}
          <div className="flex items-center gap-2 bg-[#1A1A1A] border border-gray-700 rounded-xl px-3 py-2">
            <span className="text-lg">{currentCountry?.flag}</span>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="bg-transparent text-white text-sm border-none outline-none cursor-pointer appearance-none pr-5"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23E31837' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
              data-testid="tech-country-selector"
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code} style={{ backgroundColor: '#1A1A1A' }}>
                  {c.flag} {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Page Title */}
        <div className="mb-10 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="tech-page-title">{t.subtitle}</h1>
          <p className="text-gray-400 text-sm">{t.title}</p>
        </div>

        {/* Section: ATU1650MB Device Documents */}
        <section className="mb-10" data-testid="device-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-lojack-red" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">{t.device_section}</h2>
              <p className="text-gray-500 text-xs">Documentazione Dispositivo</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-lojack-red flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{t.doc_conformity}</p>
                  <p className="text-gray-500 text-xs">ATU1650MB Declaration of Conformity.pdf</p>
                </div>
              </div>
              <a
                href={`${API}/manuals/uploads/ATU1650MB_Declaration_of_Conformity.pdf`}
                download
                className="flex items-center gap-2 px-4 py-2 bg-lojack-red/10 border border-lojack-red/30 rounded-xl text-lojack-red text-sm font-medium hover:bg-lojack-red/20 transition-colors"
                data-testid="download-doc-conformity"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">PDF</span>
              </a>
            </div>

            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-lojack-red flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{t.doc_vca}</p>
                  <p className="text-gray-500 text-xs">134120000 R10 ALL.pdf</p>
                </div>
              </div>
              <a
                href={`${API}/manuals/uploads/134120000_R10_ALL.pdf`}
                download
                className="flex items-center gap-2 px-4 py-2 bg-lojack-red/10 border border-lojack-red/30 rounded-xl text-lojack-red text-sm font-medium hover:bg-lojack-red/20 transition-colors"
                data-testid="download-doc-vca"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">PDF</span>
              </a>
            </div>
          </div>
        </section>

        {/* Section 1: Battery Device */}
        <section className="mb-10" data-testid="battery-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center">
              <Battery className="w-5 h-5 text-lojack-red" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">{t.battery_section}</h2>
              <p className="text-gray-500 text-xs">{t.reg_reference}</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-lojack-red flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{t.conformity_section}</p>
                  <p className="text-gray-500 text-xs">Battery Declaration of Conformity.pdf</p>
                </div>
              </div>
              <a
                href={`${API}/manuals/uploads/Battery_Declaration_of_Conformity.pdf`}
                download
                className="flex items-center gap-2 px-4 py-2 bg-lojack-red/10 border border-lojack-red/30 rounded-xl text-lojack-red text-sm font-medium hover:bg-lojack-red/20 transition-colors"
                data-testid="download-battery-pdf"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">PDF</span>
              </a>
            </div>
          </div>




          {/* Battery Data Table */}
          <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden mb-6">
            <div className="divide-y divide-gray-800/50">
              {[
                [t.manufacturer, batteryData.manufacturer],
                [t.trademark, batteryData.trademark],
                [t.address, batteryData.address],
                [t.website, batteryData.website, true],
                [t.email, batteryData.email, true],
                [t.model, batteryData.model],
                [t.place_manufacture, batteryData.place_manufacture],
                [t.weight, batteryData.weight],
                [t.capacity, batteryData.capacity],
                [t.chemistry, batteryData.chemistry],
                [t.hazardous, batteryData.hazardous || t.not_available],
                [t.extinguishing, batteryData.extinguishing || t.not_available],
                [t.critical_materials, batteryData.critical_materials || t.not_available],
              ].map(([label, value, isLink], idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:items-center px-5 py-3 hover:bg-white/[0.02] transition-colors">
                  <span className="text-gray-500 text-xs sm:text-sm sm:w-2/5 mb-1 sm:mb-0">{label}</span>
                  {isLink && value && value !== t.not_available ? (
                    <a href={value.startsWith('http') ? value : (value.includes('@') ? `mailto:${value}` : `https://${value}`)} target="_blank" rel="noopener noreferrer" className="text-lojack-red text-sm font-medium hover:underline flex items-center gap-1">
                      {value} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className={`text-sm font-medium ${value === t.not_available ? 'text-gray-600 italic' : 'text-white'}`}>{value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>



        </section>

        {/* Section 2: Declaration of Conformity */}
        <section className="mb-10" data-testid="conformity-section">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">{t.conformity_section}</h2>
              </div>
            </div>
            <a
              href={`${API}/technical-info/pdf/conformity`}
              download
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-400 text-sm font-medium hover:bg-blue-500/20 transition-colors"
              data-testid="download-conformity-pdf"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
            </a>
          </div>

          {conformity.content ? (
            <div className="bg-[#111] border border-gray-700 rounded-2xl p-8">
              <p className="text-gray-300 text-sm whitespace-pre-wrap" data-testid="conformity-content">{conformity.content}</p>
            </div>
          ) : (
            <div className="bg-[#111] border border-dashed border-gray-700 rounded-2xl p-8 text-center">
              <FileCheck className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 text-sm" data-testid="conformity-placeholder">{t.conformity_placeholder}</p>
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 bg-[#0A0A0A] py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-12" />
          </div>
          <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} LoJack. {t.footer}.</p>
          <a href="https://telcavoip.ai" className="text-lojack-red text-xs hover:underline">Made by TelcaVoIP AI</a>
        </div>
      </footer>
    </div>
  );
};

export default TechnicalInfoPage;
